<script lang="ts">
export default {
  name: "BaseModal",
};
</script>

<script setup lang="ts">
import BaseButton from "@/components/Base/Button.vue";
import { Modal } from "bootstrap";
import type { StyleValue } from "vue";

export type Props = {
  open?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "fullscreen";
  centered?: boolean;
  maxWidth?: boolean;
  scrollable?: boolean;
  rounded?: boolean;
  absolute?: boolean;
  coordinates?: { x: number; y: number };
  backdrop?: boolean | "static";
  keyboard?: boolean;
  embedded?: boolean;
  enableConfirmHide?: boolean;
  focus?: boolean;
  confirmHideFunc?: () => Promise<boolean>;
  title?: string;
  showCloseButton?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  open: false,
  centered: false,
  maxWidth: false,
  scrollable: false,
  rounded: false,
  absolute: false,
  backdrop: true,
  keyboard: true,
  embedded: false,
  focus: true,
  coordinates: () => ({ x: 0, y: 0 }),
  enableConfirmHide: false,
  confirmHideFunc: undefined,
  title: undefined,
  showCloseButton: true,
});

const emit = defineEmits(["hide", "show", "hidden"]);

const { t } = useI18n();

let modal: Modal | null = null;
const modalElementRef = ref<HTMLElement | null>(null);

const toggleModal = (open: boolean) => {
  if (open) {
    show();
  } else {
    hide();
  }
};

const show = () => {
  if (modal) {
    modal.show();
  }
};

const hide = () => {
  if (modal) {
    modal.hide();
  }
};

watch(
  () => props.open,
  (newValue: boolean) => {
    toggleModal(newValue);
  }
);

const isBlocked = ref(true);

const init = () => {
  if (modalElementRef.value) {
    // @ts-ignore: TODO: FixMe
    modal = new Modal(modalElementRef.value, {
      backdrop: props.backdrop,
      keyboard: props.keyboard,
      focus: props.focus,
    });

    modalElementRef.value.addEventListener("hide.bs.modal", async (event) => {
      if (props.enableConfirmHide) {
        if (isBlocked.value) {
          event.preventDefault();

          const isConfirmed =
            props.confirmHideFunc && (await props.confirmHideFunc());

          if (isConfirmed) {
            isBlocked.value = false;
            modal && modal.hide();
            emit("hide");
          }
        }
      } else {
        emit("hide");
      }
    });

    modalElementRef.value.addEventListener("hidden.bs.modal", (event) => {
      emit("hidden", event);
    });
    modalElementRef.value.addEventListener("shown.bs.modal", function () {
      emit("show");
    });
  }
};

// manuel cleanup of dom changes caused by bootstrap modal library
//
// if the component using this modal component is destroyed,
// the modal.dispose() method fails, because the modal element is already removed from the dom.
const cleanupDomElements = () => {
  const body = document.querySelector("body");
  if (body) {
    body.classList.remove("modal-open");
    body.style?.removeProperty("overflow");
    body.style?.removeProperty("padding-right");
  }
};

onMounted(() => {
  init();
  if (props.open) {
    show();
  }
});

onBeforeUnmount(() => {
  if (modal) {
    try {
      modal.dispose();
    } catch {
      // ignore
      // see cleanupDomElements()
    }
    cleanupDomElements();
  }
});

const modalDialogClasses = computed(() => {
  if (props.size === "fullscreen") {
    return `modal-${props.size}`;
  } else {
    return [
      "modal-fullscreen-sm-down",
      `modal-${props.size}`,
      {
        "modal-dialog-centered": props.centered,
        "modal-dialog--max-width": props.maxWidth,
        "modal-dialog-scrollable": props.scrollable,
        "modal-dialog--rounded": props.rounded,
        "modal-dialog--embedded": props.embedded,
      },
    ];
  }
});

const modalStyles = computed<StyleValue>(() => {
  if (props.absolute) {
    return {
      position: "absolute",
      top: `${props.coordinates.y}px`,
      left: 0,
      right: 0,
    };
  }

  return {};
});

const showDefaultHeader = computed(() => {
  return props.title || props.showCloseButton;
});

defineExpose({ show, hide });
</script>

<template>
  <div
    ref="modalElementRef"
    class="modal fade"
    tabindex="-1"
    data-testid="modal"
  >
    <div class="modal-dialog" :class="modalDialogClasses">
      <div class="modal-content" :style="modalStyles">
        <slot name="header">
          <div v-if="showDefaultHeader" class="modal-header">
            <h5 v-if="title" class="modal-title">{{ title }}</h5>
            <button
              v-if="showCloseButton"
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
        </slot>

        <div class="modal-body">
          <slot></slot>
        </div>

        <div class="modal-footer">
          <slot name="footer">
            <BaseButton rounded data-bs-dismiss="modal" type="button">
              {{ t("links.close") }}
            </BaseButton>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-content {
  box-shadow: $shadow-brand;
  background-color: $color-surface-white;

  // overwriting default bootstrap modal styles to avoid content height being to short on long mobile modals // TODO: check if this was related to the BS overwritte issue, mayb ethis can be removed
  .modal-fullscreen-sm-down & {
    height: auto;
  }
}

.modal--dark {
  .modal-content {
    background-color: transparent;
  }
}

.modal-dialog--max-width {
  max-width: 720px;
  min-height: 400px;
}

.modal-header {
  height: 70px;
  border-radius: 0;
  border-bottom: none;
  padding: $spacer-3 $spacer-4;
}

.modal-body {
  padding: 0 $spacer-4 $spacer-4;

  h5 {
    font-size: 1em;
  }

  .modal-list {
    list-style: none;
    padding-left: 1.75rem;

    li {
      margin: 0.25rem 0;
    }

    i {
      display: inline-block;
      width: 1.75rem;
      margin-left: -1.75rem;
    }
  }
}

.modal-footer {
  background-color: $border-color-light;
  border-top: none;
}

.modal-dialog--rounded .modal-content {
  border-radius: 8px;
}

.modal-dialog--absolute {
  position: absolute;
  @include media-breakpoint-down(md) {
    position: relative;
  }
}

// text-editor modal
.modal-dialog--embedded {
  --bs-modal-width: 700px;

  .modal-content {
    border: 2px solid $border-color;
    border-radius: $border-radius-lg;
    box-shadow: $brand-box-shadow;
    background-color: $color-surface-white;
    max-height: 400px;

    @include media-breakpoint-down(md) {
      max-height: 100%;
    }
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    height: auto;
  }

  .modal-title {
    font-size: 1rem;
  }

  .modal-footer {
    background-color: $color-surface-white;
  }
}

.modal-body-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: $spacer-4;

  h3 {
    width: 100%;
    margin-bottom: 0;
  }

  @include media-breakpoint-down(md) {
    margin-left: 0;
  }

  .btn-close {
    position: absolute;
    top: 0;
    right: 0;
    margin: $spacer-3;
  }
}

// pinboard card modal only
.modal-body-body {
  display: flex;
  gap: $spacer-4;
  min-height: 250px;
  padding: 0 $spacer-4;
}

// survey question_answers details modal
.modal-title--cropped {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  margin-top: $spacer-3;
  padding-bottom: 0.25rem;
}
</style>
